import React from 'react';
import { axius, types } from 'helpers';
import { notification, Spin } from 'antd';
import { LoadingMin } from './Loading';

interface Props {
    row?: { name: string; link: string };
    clear?: boolean;
    label?: string;
    accept?: string;
    acceptsPdf?: boolean;
    maxFileSizeMB?: number;
    folder: types.Folders;
    preview?: 'text' | 'image';
    className?: string;
    helperText?: any;
    helperDescription?: any;
    onSuccess?: (e: { name: string; link: string }) => void;
}

const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const CompressLinks = ({ acceptsPdf }: { acceptsPdf: boolean }) => (
    <div>
        <div className="mb-2">Compress your file using:</div>
        {acceptsPdf && <div><a href="https://www.adobe.com/africa/acrobat/online/compress-pdf.html" target="_blank" rel="noreferrer">Compress PDF</a></div>}
        <div><a href="https://www.adobe.com/express/feature/image/compress/jpg" target="_blank" rel="noreferrer">Compress JPG</a></div>
        <div><a href="https://www.adobe.com/express/feature/image/compress/png" target="_blank" rel="noreferrer">Compress PNG</a></div>
    </div>
);

const Upload = (props: Props) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { row, clear, label, accept, folder, className, helperText, helperDescription, onSuccess } = props;
    const preview = props.preview || 'image';
    const acceptsPdf = props.acceptsPdf ?? true;
    const MAX_FILE_SIZE_MB = props.maxFileSizeMB ?? 5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const [name, setName] = React.useState<string | null>(null);
    const [link, setLink] = React.useState<string | null>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [fileSize, setFileSize] = React.useState<number | null>(null);
    const [image, setImage] = React.useState<string | null>(null);
    const [upload, setUpload] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);

    React.useEffect(() => {
        if (file && upload) uploadFile();
        // eslint-disable-next-line
    }, [file, upload]);

    React.useEffect(() => {
        if (clear) clearData();
        // eslint-disable-next-line
    }, [clear]);

    React.useEffect(() => {
        if (row?.name) {
            setName(row.name);
            setLink(row.link);
            setImage(row.link);
        }
        // eslint-disable-next-line
    }, [row?.name, row?.link]);

    const changeFile = () => {
        const fale = (inputRef.current?.files || [])[0];
        if (fale) {
            if (fale.size > MAX_FILE_SIZE_BYTES) {
                notification.error({
                    message: `File too large (${formatFileSize(fale.size)}). Max file size is ${MAX_FILE_SIZE_MB}MB.`,
                    description: <CompressLinks acceptsPdf={acceptsPdf} />,
                    duration: 0,
                });
                if (inputRef.current) inputRef.current.value = '';
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setFile(fale);
                setFileSize(fale.size);
                setImage(reader.result as string);
                setUpload(true);
            };
            reader.readAsDataURL(fale);
        }
    };

    const uploadFile = () => {
        setUploading(true);
        axius.postFile(`upload?folder=${folder}`, { file, folder }).then((res: any) => {
            if (res.status === 201) {
                setName(res.data.name);
                setLink(res.data.link);
                onSuccess && onSuccess(res.data);
            } else {
                clearData();
                notification.error({ message: res.message || 'Upload failed' });
            }
            setUpload(false);
            setUploading(false);
        });
    };

    const deleteFile = () => {
        setUploading(true);
        axius.delte(`upload/${folder}/${name}`).then(() => {
            clearData();
            setUploading(false);
        });
    };

    const clearData = () => {
        setName(null);
        setLink(null);
        setFile(null);
        setFileSize(null);
        setImage(null);
        onSuccess && onSuccess({ name: '', link: '' });
    };

    return (
        <div id="Upload" className={`border border-gray-200 rounded-[8px] ${className || ''}`}>
            <input
                ref={inputRef}
                type="file"
                accept={accept || 'image/*'}
                onChange={changeFile}
                style={{ display: 'none' }}
            />
            <Spin spinning={uploading} indicator={<div className="flex justify-center items-center"><LoadingMin /></div>}>
                {(image || link) ? (
                    <>
                        {preview === 'image' && (
                            <div className="p-[12px] flex justify-between items-center">
                                <div className="pr-4 w-8/12">
                                    <div className="bg-img rounded-lg" style={{ backgroundImage: `url(${image})`, width: '100%' }} />
                                    {fileSize != null && (
                                        <div className="mt-1 text-xs text-gray-400">{formatFileSize(fileSize)}</div>
                                    )}
                                </div>
                                <div className="w-4/12">
                                    <div className="mb-2">
                                        <div
                                            className="p-2 rounded-lg border block text-center cursor-pointer hover:bg-gray-100 transi text-sm"
                                            onClick={() => inputRef.current?.click()}
                                        >
                                            Replace
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className="p-2 rounded-lg border block text-center cursor-pointer hover:bg-gray-100 text-red-600 transi text-sm"
                                            onClick={deleteFile}
                                        >
                                            Delete
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {preview === 'text' && name && (
                            <div className="p-4">
                                {label && <div className="mb-2 font-medium">{label}</div>}
                                <div className="mb-5 flex justify-between items-center">
                                    <div className="truncate w-6/12 text-sm text-gray-500">
                                        {name}
                                        {fileSize != null && <span className="text-gray-400"> ({formatFileSize(fileSize)})</span>}
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="font-medium cursor-pointer primary" onClick={() => window.open(link!, '_blank')}>View</div>
                                        <div className="font-medium cursor-pointer danger" onClick={deleteFile}>Delete</div>
                                    </div>
                                </div>
                                <div
                                    className="border border-dashed text-center p-2 text-gray-500 rounded-lg cursor-pointer transi hover:border-primary hover:text-primary"
                                    onClick={() => inputRef.current?.click()}
                                >
                                    Replace file
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div
                        onClick={() => inputRef.current?.click()}
                        className="rounded-[8px] text-center cursor-pointer hover:bg-gray-50 p-[12px]"
                    >
                        <div className="text-gray-400 text-2xl mb-1">↑</div>
                        <div className="mb-1 text-sm">
                            {helperText || <><span className="primary font-medium">Click to upload</span> or drag and drop</>}
                        </div>
                        <div className="text-xs text-gray-400">
                            {helperDescription || 'PNG, JPG (max. 800x400px)'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                            Max file size: {MAX_FILE_SIZE_MB}MB. If your file is larger,{' '}
                            {acceptsPdf && (
                                <>
                                    <a
                                        href="https://www.adobe.com/africa/acrobat/online/compress-pdf.html"
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        compress PDF
                                    </a>,{' '}
                                </>
                            )}
                            <a
                                href="https://www.adobe.com/express/feature/image/compress/jpg"
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                compress JPG
                            </a>{' '}
                            or{' '}
                            <a
                                href="https://www.adobe.com/express/feature/image/compress/png"
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                compress PNG
                            </a>.
                        </div>
                    </div>
                )}

            </Spin>
        </div>
    );
};

export default Upload;
